import React, { useLayoutEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ClerkProvider, SignedIn, SignedOut, RedirectToSignIn, useAuth, ClerkLoading, ClerkLoaded } from '@clerk/clerk-react';
import { dark } from '@clerk/themes';
import App from './App';
import { BetaApp } from '@/beta/App';
import { setTokenProvider } from './services/api';
import { useAuthStore } from './store/authStore';
import { PairingScreen } from './components/pairing/PairingScreen';
import './styles/globals.css';

/**
 * VITE_REQUIRE_PAIRING=true enables kiosk mode:
 *   - Clerk is skipped entirely
 *   - PairingScreen shows until device is paired
 *   - All API calls use the persisted vm_kiosk_ Bearer token
 *
 * Without VITE_REQUIRE_PAIRING (local development):
 *   - ClerkProvider wraps the app as before
 *   - Developer logs in with Clerk credentials
 */
const REQUIRE_PAIRING = import.meta.env.VITE_REQUIRE_PAIRING === 'true';
const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

if (!REQUIRE_PAIRING && !CLERK_PUBLISHABLE_KEY) {
  throw new Error('Missing VITE_CLERK_PUBLISHABLE_KEY environment variable');
}

/**
 * TokenSync bridges Clerk authentication to the API layer.
 * It injects Clerk's getToken function into api.ts so that all API calls
 * automatically include Bearer tokens without refactoring individual consumers.
 */
function TokenSync({ children }: { children: React.ReactNode }) {
  const { getToken } = useAuth();

  // useLayoutEffect ensures the token provider is injected before any child
  // useEffect runs (React fires layout effects parent-before-children, then
  // regular effects children-before-parent). Without this, child components
  // like VehicleScopeSelector fire API calls before the token is available,
  // causing Clerk middleware to reject with 404.
  useLayoutEffect(() => {
    setTokenProvider(getToken);
  }, [getToken]);

  return <>{children}</>;
}

/**
 * WorkshopLoadingSkeleton shows during Clerk initialization.
 * Renders a dark-themed skeleton matching the workshop app layout.
 */
function WorkshopLoadingSkeleton() {
  return (
    <div className="min-h-screen bg-workshop-bg">
      <header className="sticky top-0 z-30 bg-workshop-bg/95 backdrop-blur border-b border-workshop-border">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded bg-workshop-surface animate-pulse" />
              <div className="h-8 w-32 rounded bg-workshop-surface animate-pulse" />
            </div>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-workshop-surface animate-pulse" />
              <div className="h-10 w-20 rounded-lg bg-workshop-surface animate-pulse" />
              <div className="h-10 w-10 rounded-full bg-workshop-surface animate-pulse" />
            </div>
          </div>
          <div className="h-12 w-full rounded-xl bg-workshop-surface animate-pulse" />
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="aspect-square rounded-xl bg-workshop-surface animate-pulse" />
          ))}
        </div>
      </main>
    </div>
  );
}

/**
 * KioskRoot handles the kiosk/production mode rendering.
 *
 * Reads isPaired from the persisted authStore:
 *   - Unpaired: show PairingScreen (blocks until code is entered)
 *   - Paired: render App directly (no Clerk, token injected via authStore)
 *
 * This component subscribes to the store so re-renders when isPaired changes
 * (e.g. after a successful pairing or a 401 token revocation).
 *
 * NOTE: /beta/* is mounted OUTSIDE this component via the router in the render
 * call, so beta is reachable without pairing (Phase B smoke screen has no API calls).
 */
function KioskRoot() {
  const isPaired = useAuthStore((s) => s.isPaired);

  if (!isPaired) {
    return <PairingScreen />;
  }

  return <App />;
}

// ---------------------------------------------------------------------------
// Render
// ---------------------------------------------------------------------------

const root = ReactDOM.createRoot(document.getElementById('root')!);

if (REQUIRE_PAIRING) {
  // Kiosk / production mode: skip Clerk entirely.
  // BrowserRouter wraps both /beta/* (public) and the catch-all legacy kiosk.
  root.render(
    <React.StrictMode>
      <BrowserRouter>
        <Routes>
          <Route path="/beta/*" element={<BetaApp />} />
          <Route path="*" element={<KioskRoot />} />
        </Routes>
      </BrowserRouter>
    </React.StrictMode>
  );
} else {
  // Development / Clerk mode: full Clerk provider wrapping both trees.
  root.render(
    <React.StrictMode>
      <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY!} appearance={{ baseTheme: dark }}>
        <BrowserRouter>
          <Routes>
            <Route path="/beta/*" element={<BetaApp />} />
            <Route
              path="*"
              element={
                <>
                  <ClerkLoading>
                    <WorkshopLoadingSkeleton />
                  </ClerkLoading>
                  <ClerkLoaded>
                    <SignedIn>
                      <TokenSync>
                        <App />
                      </TokenSync>
                    </SignedIn>
                    <SignedOut>
                      <RedirectToSignIn />
                    </SignedOut>
                  </ClerkLoaded>
                </>
              }
            />
          </Routes>
        </BrowserRouter>
      </ClerkProvider>
    </React.StrictMode>
  );
}
